import React, { useEffect } from "react";
import { Claim, ClaimStatus } from "../../models/Claim";
import { useParams } from "react-router";
import authRestClient from "../../services/clients/AuthRestClient";
import { Link } from "react-router-dom";
import SkeletonCardLoad from "../../components/SkeletonCardLoad";
import { Tooltip } from "react-tooltip";
import ClaimStatusBadge from "../../components/ClaimStatusBadge";

const TravelClaim = () => {

    const { guid } = useParams();

    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [claim, setClaim] = React.useState<Claim>();
    
    useEffect(() => {
        /* Fetch data for the specified GUID from API */
        authRestClient.get<Claim>("/api/travelclaim/" + guid)
            .then(response => {
                if (response.status === 200)
                {
                    setClaim(response.data);
                }
                setIsLoading(false);
            })
            .catch(err => {
            });
    }, [guid])

    /**
     * Method renders action button for requesting unit costs or adding bank details.
     * @returns JSX.Element that represents a single action link element.
     */
    function renderEditButton(): JSX.Element {

        if (claim?.event.endDate) {

            let currentDate = new Date();
            currentDate.setHours(0, 0, 0, 0);

            /* Check if event has been finished already | Otherwise render empty tag */
            let seminarFinished = currentDate > new Date(claim.event.endDate);

            if (claim?.status === ClaimStatus.Saved && seminarFinished) {
                return <Link to={"/me/claims/" + claim.guid + "/bankdetails"} className="btn btn-warning w-100"><i className="bi bi-file-arrow-up me-2"></i>Request contribution</Link>
            }
        }
        return <></>; // Render empty tag | JSX Element for unsupported status ...
    }

    return (
        <main className="container mb-4">

            <div className="row text-center">
                <div className="col-12">
                    <img src="/brand.png" className="brand" alt="brand" />
                </div>
            </div>

            <div className="row text-center">
                <div className="col-12">
                    <h2>Unit Costs Claim Form</h2>
                </div>
                <div className="col-12">
                    <p>Overview of submitted Unit Cost Form.</p>
                </div>
            </div>

            <div className="row mb-2">
                <div className="col-12">
                    <Link to="/me/claims" className="btn btn-warning"><i className="bi bi-arrow-left"></i> Back to Overview</Link>
                </div>
            </div>

            {
                isLoading ? <SkeletonCardLoad cardsCount={1} /> 
                          :
                <div className="card">

                    <div className="row g-0">

                        <div className="col-12 col-md-8">
                            <div className="card-body">

                                { /* Render Claim Informations */}

                                <div className="row">
                                     <div className="col-12">
                                        <ClaimStatusBadge status={claim?.status} />
                                    </div>
                                </div>

                                <hr />

                                <div className="row text-center">
                                    <div className="col-12">
                                        <Tooltip id="reimbursementTTip" />
                                        <label className="display-4 fw-bold" data-tooltip-id="reimbursementTTip" data-tooltip-content="Total Reimbursement">&euro; {(claim?.calculatedTravelCosts?.amount ?? 0) + (claim?.calculatedAccommodationCosts?.totalAmount ?? 0)}</label>
                                    </div>
                                </div>

                                <hr />

                                <dl className="row">
                                    <dt className="col-6 col-md-12">
                                        <label><i className="bi bi-person me-2"></i>Participant</label>
                                    </dt>
                                    <dd className="col-6 col-md-12">
                                        {claim?.lastname + ", " + claim?.firstname}
                                    </dd>
                                </dl>

                                <dl className="row">
                                    <dt className="col-6 col-md-12">
                                        <label><i className="bi bi-envelope me-2"></i>Email</label>
                                    </dt>
                                    <dd className="col-6 col-md-12">
                                        <Link to="#">{claim?.email}</Link>
                                    </dd>
                                </dl>

                                <dl className="row">
                                    <dt className="col-6 col-md-12">
                                        <label><i className="bi bi-phone me-2"></i>Phone</label>
                                    </dt>
                                    <dd className="col-6 col-md-12">
                                        {claim?.phone}
                                    </dd>
                                </dl>

                                <dl className="row">
                                    <dt className="col-6 col-md-12">
                                        <label><i className="bi bi-arrow-right me-2"></i>Your place of employment</label>
                                    </dt>
                                    <dd className="col-6 col-md-12">
                                        {claim?.institution.name} - {claim?.institution.address.street} - {claim?.institution.address.city}
                                    </dd>
                                </dl>

                                <dl className="row">
                                    <dt className="col-6 col-md-12">
                                        <label><i className="bi bi-arrow-left me-2"></i>Event Location</label>
                                    </dt>
                                    <dd className="col-6 col-md-12">
                                        {claim?.event.location}
                                    </dd>
                                </dl>

                            </div>
                        </div>

                        <div className="col-12 col-md-4 bg-light py-2 px-4">

                            { /* Render Calculated Cost Informations + Event Informations */}

                            <div className="row mt-1">
                                    <div className="col-12">
                                        <Tooltip id="travelCostTTip" />
                                        <span className="badge bg-dark badge-lg p-2" data-tooltip-id="travelCostTTip" data-tooltip-content={"Distance travelled by " + (claim?.calculatedTravelCosts?.travelType === 0 ? "airplane" : "land transport")} data-tooltip-place="bottom">
                                        <i className={claim?.calculatedTravelCosts?.travelType === 0 ? "bi bi-airplane me-2" : "bi bi-train-front me-2"}></i>
                                            {Math.round(claim?.calculatedTravelCosts?.distance ?? 0)} km | &euro; {claim?.calculatedTravelCosts?.amount}
                                    </span>
                                    {claim?.calculatedAccommodationCosts &&
                                        <>
                                            <Tooltip id="accommodationCostTTip" />
                                            <span className="badge bg-dark badge-lg ms-2 p-2" data-tooltip-id="accommodationCostTTip" data-tooltip-content="Nights spend" data-tooltip-place="bottom">
                                                <i className="bi bi-house me-2"></i>
                                                {claim.calculatedAccommodationCosts?.nights} | {claim.calculatedAccommodationCosts.amount} &euro;
                                            </span>
                                        </>
                                    }
                                </div>
                            </div>

                            <hr />

                            <dl className="row">
                                <dt className="col-6 col-md-12">
                                    <label>Event Title</label>
                                </dt>
                                <dd className="col-6 col-md-12">
                                    {claim?.event.title}
                                </dd>
                            </dl>

                            <dl className="row">
                                <dt className="col-6 col-md-12">
                                    <label>Event Number</label>
                                </dt>
                                <dd className="col-6 col-md-12">
                                    {claim?.event.number}
                                </dd>
                            </dl>

                            <dl className="row">
                                <dt className="col-6 col-md-12">
                                    <label><i className="bi bi-calendar me-2"></i>Seminar Start Date</label>
                                </dt>
                                <dd className="col-6 col-md-12">
                                    {new Date(claim?.event.startDate ?? "").toDateString()}
                                </dd>
                            </dl>

                            <dl className="row">
                                <dt className="col-6 col-md-12">
                                    <label><i className="bi bi-calendar me-2"></i>Seminar End Date</label>
                                </dt>
                                <dd className="col-6 col-md-12">
                                    {new Date(claim?.event.endDate ?? "").toDateString()}
                                </dd>
                            </dl>

                            <dl className="row">
                                <dt className="col-6 col-md-12">
                                    <label><i className="bi bi-geo me-2"></i>Location</label>
                                </dt>
                                <dd className="col-6 col-md-12">
                                    {claim?.event.location}
                                </dd>
                            </dl>

                                {(claim?.status === ClaimStatus.Saved) && 
                                <>
                                    <hr />
                                    <div className="row">
                                        <div className="col-12">
                                            {renderEditButton()}
                                        </div>
                                    </div>
                                </>
                            }

                        </div>

                    </div>

                    {claim?.elvData &&
                        <div className="row g-0 border-top">

                            <div className="col-12">
                                <div className="card-body">

                                    <dl className="row">
                                        <dt className="col-12 col-md-4">
                                            <label>IBAN</label>
                                        </dt>
                                        <dd className="col-12 col-md-8">
                                            {claim?.elvData.iban}
                                        </dd>
                                    </dl>
                                    <dl className="row">
                                        <dt className="col-12 col-md-4">
                                            <label>BIC</label>
                                        </dt>
                                        <dd className="col-12 col-md-8">
                                            {claim?.elvData.bic}
                                        </dd>
                                    </dl>
                                    <dl className="row">
                                        <dt className="col-12 col-md-4">
                                            <label>Bankname</label>
                                        </dt>
                                        <dd className="col-12 col-md-8">
                                            {claim?.elvData.bankname}
                                        </dd>
                                    </dl>

                                    {claim?.elvData.bankAddress &&
                                        <dl className="row">
                                            <dt className="col-12 col-md-4">
                                                <label>Bankaddress</label>
                                            </dt>
                                            <dd className="col-12 col-md-8">
                                                {claim?.elvData.bankAddress.street} - {claim?.elvData.bankAddress.postalCode} - {claim?.elvData.bankAddress.city} - {claim?.elvData.bankAddress.country}
                                            </dd>
                                        </dl>
                                    }

                                    <hr />

                                    <dl className="row">
                                        <dt className="col-12 col-md-4">
                                            <label>Account Holder</label>
                                        </dt>
                                        <dd className="col-12 col-md-8">
                                            {claim?.elvData.accountHolderName}
                                        </dd>
                                    </dl>
                                    <dl className="row">
                                        <dt className="col-12 col-md-4">
                                            <label>Account Holder Address</label>
                                        </dt>
                                        <dd className="col-12 col-md-8">
                                            {claim?.elvData.accountHolderAddress.street} - {claim?.elvData.accountHolderAddress.postalCode} - {claim?.elvData.accountHolderAddress.city} - {claim?.elvData.accountHolderAddress.country}
                                        </dd>
                                    </dl>

                                </div>
                            </div>

                        </div>
                    }
                </div>
            }
        </main>
    )
}

export default TravelClaim;